import _ from 'lodash'
import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Dropdown, Form, Label, Menu, Segment } from 'semantic-ui-react'
import SkillTabContent from './SkillTabContent'
import SkillAddModal from './SkillAddModal'
import useSkillModal from './useSkillModal'
import { diffSkill } from './skillsTabUtils'
import './SkillsTabPanel.css'

const SKILL_TYPE_TECHNICAL = 'technical'
const SKILL_TYPE_SOFT = 'soft'
const SKILL_TYPE_LANGUAGE = 'language'

const LEGENDS = [
  '1 = Basic, 2 = Satisfactory, 3 = Good, 4 = Excellent, 5 = Outstanding',
  '1 = Basic, 2 = Satisfactory, 3 = Good, 4 = Excellent, 5 = Outstanding',
  '1 = A2 Base, 2 = B1 School, 3 = B2 Intermediate, 4 = C1 Advanced, 5 = C2 Native Speaker',
]

const MODAL_HEADERS = {
  [SKILL_TYPE_TECHNICAL]: 'Add Technical Skill',
  [SKILL_TYPE_SOFT]: 'Add Soft Skill',
  [SKILL_TYPE_LANGUAGE]: 'Add Language',
}

const TAB_CONFIG = [
  { type: SKILL_TYPE_TECHNICAL, label: 'Technical Skill', tabId: 'skills-tab-technical', panelId: 'skills-tabpanel-technical' },
  { type: SKILL_TYPE_SOFT, label: 'Soft Skill', tabId: 'skills-tab-soft', panelId: 'skills-tabpanel-soft' },
  { type: SKILL_TYPE_LANGUAGE, label: 'Language', tabId: 'skills-tab-language', panelId: 'skills-tabpanel-language' },
]

const TAB_COUNT = TAB_CONFIG.length

/**
 * Tab panel for managing Technical Skills, Soft Skills, and Languages in the CV.
 * Uses a data-driven approach to render tabs and their content panels.
 *
 * Props:
 *   editable                    {boolean}   Whether the user can add/edit/delete (true on /cvGenerator)
 *   categoriesOptions           {Array}     Dropdown options for technical skill categories
 *   technicalSkillsOptions      {Array}     Dropdown options for technical skills (filtered by category)
 *   softSkillsOptions           {Array}     Dropdown options for soft skills
 *   languagesOptions            {Array}     Dropdown options for languages
 *   userTechnicalSkillsRaw      {Array}     User's technical skills (raw data with id, technical_skill, level)
 *   userSoftSkillsRaw           {Array}     User's soft skills (raw data with id, soft_skill, level)
 *   userLanguagesRaw            {Array}     User's languages (raw data with id, language, level)
 *   userTechnicalSkillsOptions  {Array}     User's technical skills for diff filtering
 *   userSoftSkillsOptions       {Array}     User's soft skills for diff filtering
 *   userLanguagesOptions        {Array}     User's languages for diff filtering
 *   getTechnicalSkills          {Function}  Fetch technical skills by category id
 *   onAddTechnicalSkill         {Function}  fn(skillId, level) — may return a Promise
 *   onEditTechnicalSkill        {Function}  fn(skillId, newLevel)
 *   onDeleteTechnicalSkill      {Function}  fn(skillId)
 *   onAddSoftSkill              {Function}  fn(skillId, level) — may return a Promise
 *   onEditSoftSkill             {Function}  fn(skillId, newLevel)
 *   onDeleteSoftSkill           {Function}  fn(skillId)
 *   onAddLanguage               {Function}  fn(languageId, level) — may return a Promise
 *   onEditLanguage              {Function}  fn(languageId, newLevel)
 *   onDeleteLanguage            {Function}  fn(languageId)
 */
const SkillsTabPanel = ({
  editable,
  categoriesOptions = [],
  technicalSkillsOptions = [],
  softSkillsOptions = [],
  languagesOptions = [],
  userTechnicalSkillsRaw = [],
  userSoftSkillsRaw = [],
  userLanguagesRaw = [],
  userTechnicalSkillsOptions = [],
  userSoftSkillsOptions = [],
  userLanguagesOptions = [],
  getTechnicalSkills,
  onAddTechnicalSkill,
  onEditTechnicalSkill,
  onDeleteTechnicalSkill,
  onAddSoftSkill,
  onEditSoftSkill,
  onDeleteSoftSkill,
  onAddLanguage,
  onEditLanguage,
  onDeleteLanguage,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState(null)

  const { state: modalState, openModal, handleRate, handleConfirm, handleClose } = useSkillModal({
    [SKILL_TYPE_TECHNICAL]: onAddTechnicalSkill,
    [SKILL_TYPE_SOFT]: onAddSoftSkill,
    [SKILL_TYPE_LANGUAGE]: onAddLanguage,
  })

  // --- Memoized list data ---
  const technicalItems = useMemo(
    () => _.toArray(userTechnicalSkillsRaw).map((item) => ({ id: item.id, name: item.technical_skill, level: item.level })),
    [userTechnicalSkillsRaw]
  )

  const softItems = useMemo(
    () => _.toArray(userSoftSkillsRaw).map((item) => ({ id: item.id, name: item.soft_skill, level: item.level })),
    [userSoftSkillsRaw]
  )

  const languageItems = useMemo(
    () => _.toArray(userLanguagesRaw).map((item) => ({ id: item.id, name: item.language, level: item.level })),
    [userLanguagesRaw]
  )

  // --- Memoized diff options ---
  const filteredTechnicalOptions = useMemo(
    () => diffSkill(technicalSkillsOptions, userTechnicalSkillsOptions),
    [technicalSkillsOptions, userTechnicalSkillsOptions]
  )

  const filteredSoftOptions = useMemo(
    () => diffSkill(softSkillsOptions, userSoftSkillsOptions),
    [softSkillsOptions, userSoftSkillsOptions]
  )

  const filteredLanguageOptions = useMemo(
    () => diffSkill(languagesOptions, userLanguagesOptions),
    [languagesOptions, userLanguagesOptions]
  )

  // --- Tab data map (memoized to avoid re-creation on every render) ---
  const tabDataMap = useMemo(() => ({
    [SKILL_TYPE_TECHNICAL]: {
      items: technicalItems,
      options: selectedCategory ? filteredTechnicalOptions : [],
      placeholder: 'Add new Technical Skill',
      legend: LEGENDS[0],
      onLevelChange: onEditTechnicalSkill,
      onDelete: onDeleteTechnicalSkill,
    },
    [SKILL_TYPE_SOFT]: {
      items: softItems,
      options: filteredSoftOptions,
      placeholder: 'Add new Soft Skill',
      legend: LEGENDS[1],
      onLevelChange: onEditSoftSkill,
      onDelete: onDeleteSoftSkill,
    },
    [SKILL_TYPE_LANGUAGE]: {
      items: languageItems,
      options: filteredLanguageOptions,
      placeholder: 'Add new Language',
      legend: LEGENDS[2],
      onLevelChange: onEditLanguage,
      onDelete: onDeleteLanguage,
    },
  }), [
    technicalItems, softItems, languageItems,
    selectedCategory, filteredTechnicalOptions, filteredSoftOptions, filteredLanguageOptions,
    onEditTechnicalSkill, onDeleteTechnicalSkill,
    onEditSoftSkill, onDeleteSoftSkill,
    onEditLanguage, onDeleteLanguage,
  ])

  // --- Factory for dropdown select handlers (replaces three identical useCallback) ---
  const makeDropdownSelectHandler = useCallback((type) => (_e, data) => {
    const selectedOpt = data.options.find((opt) => opt.value === data.value)
    if (!selectedOpt || !selectedOpt.key) return
    openModal(type, data.value, selectedOpt.key)
  }, [openModal])

  const dropdownHandlers = useMemo(() => ({
    [SKILL_TYPE_TECHNICAL]: makeDropdownSelectHandler(SKILL_TYPE_TECHNICAL),
    [SKILL_TYPE_SOFT]: makeDropdownSelectHandler(SKILL_TYPE_SOFT),
    [SKILL_TYPE_LANGUAGE]: makeDropdownSelectHandler(SKILL_TYPE_LANGUAGE),
  }), [makeDropdownSelectHandler])

  // --- Tab click handler (resets category when switching away from technical) ---
  const handleTabClick = useCallback((_e, data) => {
    setActiveTab(data.index)
    // Reset category selection when leaving technical tab to avoid stale state
    if (TAB_CONFIG[data.index].type !== SKILL_TYPE_TECHNICAL) {
      setSelectedCategory(null)
    }
  }, [])

  // --- Keyboard navigation between tabs (arrow left/right, Home, End) ---
  const handleTabKeyDown = useCallback((e) => {
    let nextIndex = null
    if (e.key === 'ArrowRight') {
      nextIndex = (activeTab + 1) % TAB_COUNT
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (activeTab - 1 + TAB_COUNT) % TAB_COUNT
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = TAB_COUNT - 1
    }

    if (nextIndex !== null) {
      e.preventDefault()
      setActiveTab(nextIndex)
      if (TAB_CONFIG[nextIndex].type !== SKILL_TYPE_TECHNICAL) {
        setSelectedCategory(null)
      }
      const tabElement = document.getElementById(TAB_CONFIG[nextIndex].tabId)
      if (tabElement) tabElement.focus()
    }
  }, [activeTab])

  // --- Category selection (Technical Skill) ---
  const handleCategoryChange = useCallback((_e, data) => {
    const selectedOpt = data.options.find((opt) => opt.value === data.value)
    if (selectedOpt && selectedOpt.key != null) {
      setSelectedCategory(selectedOpt.value)
      getTechnicalSkills(selectedOpt.key)
    }
  }, [getTechnicalSkills])

  // --- Active tab config and data ---
  const activeConfig = TAB_CONFIG[activeTab]
  const activeData = tabDataMap[activeConfig.type]

  // --- Category section (only rendered for Technical tab) ---
  const categorySection = activeConfig.type === SKILL_TYPE_TECHNICAL
    ? (
      <Form.Field>
        <Dropdown
          placeholder="Select a category"
          search
          selection
          options={categoriesOptions}
          onChange={handleCategoryChange}
          className="skills-tab-dropdown"
          aria-label="Select skill category"
        />
      </Form.Field>
    )
    : undefined

  return (
    <Segment className="skills-tab-panel">
      <Menu
        pointing
        secondary
        className="skills-tab-menu"
        role="tablist"
        aria-label="Skills tabs"
        onKeyDown={handleTabKeyDown}
      >
        {TAB_CONFIG.map((tab, index) => (
          <Menu.Item
            key={tab.tabId}
            id={tab.tabId}
            index={index}
            active={activeTab === index}
            onClick={handleTabClick}
            className="skills-tab-menu-item"
            role="tab"
            aria-selected={activeTab === index}
            aria-controls={tab.panelId}
            tabIndex={activeTab === index ? 0 : -1}
          >
            {tab.label}
            <Label circular size="mini" color="teal" className="skills-tab-badge">
              {tabDataMap[tab.type].items.length}
            </Label>
          </Menu.Item>
        ))}
      </Menu>

      <SkillTabContent
        editable={editable}
        items={activeData.items}
        options={activeData.options}
        placeholder={activeData.placeholder}
        legend={activeData.legend}
        onDropdownSelect={dropdownHandlers[activeConfig.type]}
        onLevelChange={activeData.onLevelChange}
        onDelete={activeData.onDelete}
        categorySection={categorySection}
        tabId={activeConfig.tabId}
        panelId={activeConfig.panelId}
      />

      <SkillAddModal
        isOpen={modalState.isOpen}
        isSubmitting={modalState.isSubmitting}
        header={MODAL_HEADERS[modalState.type] || ''}
        name={modalState.name}
        level={modalState.level}
        onRate={handleRate}
        onConfirm={handleConfirm}
        onClose={handleClose}
      />
    </Segment>
  )
}

SkillsTabPanel.propTypes = {
  editable: PropTypes.bool.isRequired,
  categoriesOptions: PropTypes.array,
  technicalSkillsOptions: PropTypes.array,
  softSkillsOptions: PropTypes.array,
  languagesOptions: PropTypes.array,
  userTechnicalSkillsRaw: PropTypes.array,
  userSoftSkillsRaw: PropTypes.array,
  userLanguagesRaw: PropTypes.array,
  userTechnicalSkillsOptions: PropTypes.array,
  userSoftSkillsOptions: PropTypes.array,
  userLanguagesOptions: PropTypes.array,
  getTechnicalSkills: PropTypes.func.isRequired,
  onAddTechnicalSkill: PropTypes.func.isRequired,
  onEditTechnicalSkill: PropTypes.func.isRequired,
  onDeleteTechnicalSkill: PropTypes.func.isRequired,
  onAddSoftSkill: PropTypes.func.isRequired,
  onEditSoftSkill: PropTypes.func.isRequired,
  onDeleteSoftSkill: PropTypes.func.isRequired,
  onAddLanguage: PropTypes.func.isRequired,
  onEditLanguage: PropTypes.func.isRequired,
  onDeleteLanguage: PropTypes.func.isRequired,
}

export default SkillsTabPanel
